import { Fragment, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Rocket, XCircle, RefreshCw, AlertTriangle, ExternalLink,
  Terminal, Package, Clock, Server, Globe, GitBranch, CheckCircle2,
  Loader2, AlertCircle, ShieldCheck, Ticket, Sparkles, MinusCircle,
} from 'lucide-react'
import clsx from 'clsx'
import {
  useDeployment, useDeploymentLogs, useTriggerDeployment, useCancelDeployment,
  useSwitchTraffic, usePipelineRun, useAppPipelineRun,
} from '@/hooks/useDeployments'
import { StatusBadge } from '@/components/common/StatusBadge'
import { SizeBadge } from '@/components/common/SizeBadge'
import { Button } from '@/components/common/Button'
import { Card, CardHeader, CardContent } from '@/components/common/Card'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { EmptyState } from '@/components/common/EmptyState'
import { formatDateTime, formatRelative, formatStrategy, formatDeploymentStatus } from '@/utils/format'
import { isAppriseDeployment } from '@/utils/apprise'
import { placementCards } from '@/utils/deploymentInfo'
import { AppriseDeploymentPanel } from '@/components/apprise-vm/AppriseDeploymentPanel'
import { api } from '@/services/api'
import type {
  DeploymentStatus, LogLevel, PipelineRun, PipelineRunJob, GhRunStatus, GhConclusion,
} from '@/types'
import styles from './DeploymentDetailPage.module.css'

// Live status text + colour for one app server's ERP-install run (multi-server view).
function appServerStatus(
  status: GhRunStatus | null | undefined,
  conclusion: GhConclusion | null | undefined,
): { text: string; color: string } {
  if (status === 'completed') {
    if (conclusion === 'success') return { text: 'Succeeded', color: 'var(--aptean-success)' }
    if (conclusion === 'failure' || conclusion === 'timed_out') return { text: 'Failed', color: 'var(--aptean-danger, #dc2626)' }
    if (conclusion === 'cancelled') return { text: 'Cancelled', color: 'var(--aptean-text-muted, #6b7280)' }
    return { text: conclusion ?? 'Completed', color: 'var(--aptean-text-muted, #6b7280)' }
  }
  if (status === 'in_progress') return { text: 'Running', color: 'var(--aptean-warning, #d97706)' }
  if (status === 'queued') return { text: 'Queued', color: 'var(--aptean-text-muted, #6b7280)' }
  return { text: 'Pending', color: 'var(--aptean-text-muted, #6b7280)' }
}

// ─── Progress percentages ─────────────────────────────────────────────────────

const STATUS_PROGRESS: Partial<Record<DeploymentStatus, number>> & Record<string, number> = {
  pending:                    5,
  validating:                 10,
  provisioning_network:       20,
  provisioning_infra:         32,
  planning_infra:             22,
  awaiting_plan_approval:     35,
  applying_infra:             45,
  verifying_server:           85,
  infra_ready:                100,
  deploying_app:              50,
  configuring:                62,
  configuring_haproxy:        70,
  configuring_cloudflare:     76,
  post_deploy_config:         82,
  configuring_integrations:   88,
  switching_traffic:          93,
  running_health_checks:      97,
  completed:                  100,
  failed:                     100,
  rolled_back:                100,
  cancelled:                  100,
}

const LOG_LEVEL_COLORS: Record<LogLevel, string> = {
  INFO: 'var(--aptean-text-secondary)',
  DEBUG: 'var(--aptean-text-muted)',
  WARNING: 'var(--aptean-warning)',
  ERROR: 'var(--aptean-error)',
}

// ─── Duration helper ──────────────────────────────────────────────────────────

function formatDuration(startedAt: string | null, completedAt: string | null): string | null {
  if (!startedAt || !completedAt) return null
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime()
  if (ms < 0) return null
  const secs = Math.floor(ms / 1000)
  if (secs < 60) return `${secs}s`
  const mins = Math.floor(secs / 60)
  const rem = secs % 60
  return rem > 0 ? `${mins}m ${rem}s` : `${mins}m`
}


type PhaseState = 'done' | 'active' | 'failed' | 'pending'

// ─── Main Page ────────────────────────────────────────────────────────────────

export function DeploymentDetailPage() {
  const { id } = useParams<{ id: string }>()
  const deploymentId = Number(id)
  const navigate = useNavigate()
  const logsEndRef = useRef<HTMLDivElement>(null)
  const infraPanelRef = useRef<HTMLDivElement>(null)
  const appPanelRef = useRef<HTMLDivElement>(null)
  const infraScrolled = useRef(false)
  const appScrolled = useRef(false)
  const [showTicketModal, setShowTicketModal] = useState(false)
  const [ticketCreating, setTicketCreating] = useState(false)
  const [ticketCreated, setTicketCreated] = useState<string | null>(null)
  const [approving, setApproving] = useState(false)
  const [approvalNotes, setApprovalNotes] = useState('')
  const [showApprovePanel, setShowApprovePanel] = useState(false)
  const [switchNotes, setSwitchNotes] = useState('')
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false)

  // infra_ready is terminal for Apprise-on-VM (VM provisioned and verified; the application is never installed).
  const TERMINAL_STATUSES: string[] = ['completed', 'failed', 'cancelled', 'rolled_back', 'infra_ready']
  const { data: deployment, isLoading, isError, refetch } = useDeployment(deploymentId, {
    // Poll while progressing. For Ross (has_app_stage), keep polling only after
    // status hits 'completed' until app_pipeline_run_url is set — the GitHub
    // webhook fires after the orchestrator marks infra done, and we need to pick
    // up the app run URL it writes. Genuinely terminal states (failed/cancelled/
    // rolled_back) stop polling immediately rather than spinning forever waiting
    // on an app run that's never coming.
    refetchInterval: (query) => {
      const d = query.state.data
      if (!d) return false
      if (!TERMINAL_STATUSES.includes(d.status)) return 4000
      if (d.status === 'completed' && d.has_app_stage && !d.app_pipeline_run_url) return 4000
      return false
    },
  })

  // "Running" = actively progressing (not pending, not terminal). Used for the
  // Live indicator, log polling, and Cancel button.
  const isRunning = deployment
    ? !TERMINAL_STATUSES.includes(deployment.status) && deployment.status !== 'pending'
    : false

  // The real GitHub Actions run is the source of truth for progress.
  const ghEnabled = !!deployment?.pipeline_run_url && deployment?.pipeline_type === 'github_actions'
  // For Ross, keep polling the infra run past completion until the app run URL
  // appears: the pipeline-run endpoint chains the ADO app deploy as a fallback
  // when the GitHub webhook never fires, so it needs repeated chances to trigger.
  // This must cover BOTH 'completed' and 'deploying_app': once the Ansible
  // bootstrap succeeds the orchestrator parks the deployment at 'deploying_app'
  // while it waits for the app-server VM's ADO agent to come online. If we only
  // polled on 'completed', the app-deploy trigger would be attempted once (on
  // page mount) and never retried — leaving the deployment stuck at "deploying
  // app" forever even though the agent came online moments later. A
  // failed/cancelled deployment will never get an app run, so those stop polling.
  const keepInfraPolling = !!deployment?.has_app_stage
    && !deployment?.app_pipeline_run_url
    && ['completed', 'deploying_app'].includes(deployment?.status ?? '')
  const { data: pipelineRun } = usePipelineRun(deploymentId, ghEnabled, keepInfraPolling)
  // Application-deploy run (Ross → Azure DevOps 1522), tracked the same way as infra.
  const { data: appRun, isError: appRunError, isFetching: appRunFetching } = useAppPipelineRun(deploymentId, !!deployment?.app_pipeline_run_url)
  const switchTraffic = useSwitchTraffic()

  const handleApprove = async () => {
    setApproving(true)
    try {
      await api.post(`/deployments/requests/${deploymentId}/approve/`, { notes: approvalNotes })
      await api.post(`/deployments/requests/${deploymentId}/trigger/`)
      refetch()
      setShowApprovePanel(false)
    } finally { setApproving(false) }
  }

  const handleReject = async () => {
    setApproving(true)
    try {
      await api.post(`/deployments/requests/${deploymentId}/reject/`, { notes: approvalNotes })
      refetch()
      setShowApprovePanel(false)
    } finally { setApproving(false) }
  }

  const handleCreateTicket = async (data: { title: string; description: string; priority: string }) => {
    setTicketCreating(true)
    try {
      const res = await api.post<{ ticket_number: string }>(
        `/deployments/requests/${deploymentId}/create_ticket/`, data
      )
      setTicketCreated(res.data.ticket_number)
      setShowTicketModal(false)
      refetch()
    } finally { setTicketCreating(false) }
  }

  const handleSwitchTraffic = () => {
    setShowSwitchConfirm(true)
  }

  const confirmSwitchTraffic = () => {
    switchTraffic.mutate({ id: deploymentId, notes: switchNotes })
    setShowSwitchConfirm(false)
    setSwitchNotes('')
  }

  const { data: logs, isLoading: logsLoading } = useDeploymentLogs(deploymentId, {
    refetchInterval: isRunning ? 5000 : false,
  })

  const triggerMutation = useTriggerDeployment()
  const cancelMutation = useCancelDeployment()

  // Auto-scroll logs to bottom
  useEffect(() => {
    if (logs?.length) {
      logsEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [logs?.length])

  // Scroll to the infra pipeline panel the first time it appears
  useEffect(() => {
    if (deployment?.pipeline_run_url && !infraScrolled.current) {
      infraScrolled.current = true
      infraPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [deployment?.pipeline_run_url])

  // Scroll to app section as soon as infra run completes (waiting card appears),
  // and again when app_pipeline_run_url arrives (ADO panel replaces waiting card).
  useEffect(() => {
    if (!deployment?.has_app_stage || appScrolled.current) return
    const infraDone = pipelineRun?.status === 'completed'
    const appStarted = !!deployment?.app_pipeline_run_url
    if (infraDone || appStarted) {
      appScrolled.current = true
      setTimeout(() => {
        appPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 500)
    }
  }, [pipelineRun?.status, deployment?.app_pipeline_run_url, deployment?.has_app_stage])

  if (isLoading) {
    return <LoadingSpinner label="Loading deployment..." size="lg" />
  }

  if (isError || !deployment) {
    return (
      <EmptyState
        icon={<AlertCircle size={48} />}
        title="Deployment not found"
        description="Could not load this deployment."
        action={
          <Button onClick={() => navigate('/deployments')} icon={<ArrowLeft size={14} />}>
            Back to Deployments
          </Button>
        }
      />
    )
  }

  // Approval is needed while the deployment is still PENDING, requires approval,
  // and hasn't been approved/rejected yet — regardless of whether /trigger/ was
  // hit first to move it into 'pending_approval'.
  const needsApproval = !!deployment.approval_required
    && deployment.approval_status !== 'approved'
    && deployment.approval_status !== 'rejected'
    && deployment.status === 'pending'
  const isBlueGreen = deployment.strategy === 'blue_green'

  // The real GitHub Actions run is the source of truth for progress/outcome when
  // present (the portal orchestrator runs eagerly and finishes instantly).
  const runDone = pipelineRun?.status === 'completed'
  const runFailed = runDone && pipelineRun?.conclusion != null
    && !['success', 'skipped', 'neutral'].includes(pipelineRun.conclusion)
  const runInProgress = !!pipelineRun && !runDone

  // Ross runs three chained pipelines: infra (GitHub Actions Terraform) →
  // Ansible bootstrap (Azure DevOps 2093, creates the FIN/MAN DB users) →
  // AptBackOffice app deploy (Azure DevOps 1522). Each stage only starts once
  // the previous one SUCCEEDS, so a failure anywhere means the downstream
  // pipelines were never triggered — and the journey is only "complete" once the
  // app pipeline (1522) has been triggered (app_pipeline_run_url set). We treat
  // "app triggered" as 100%; the ERP install then runs and is tracked live in ADO.
  const hasApp = !!deployment.has_app_stage
  // Apprise-on-VM: plan → approval → apply → verify → infra_ready. The deployment is
  // never "completed" — the application track stays "not configured".
  const isApprise = isAppriseDeployment(deployment)
  const infraSuccess = !!pipelineRun && runDone && !runFailed

  // Ansible bootstrap (ADO 2093). No live job tracking — its state comes from the
  // completion callback (pipeline_config.ansible_bootstrap) plus whether infra
  // succeeded (which is what triggers 2093) and the run URL the trigger recorded.
  const ansibleStatus = deployment.pipeline_config?.ansible_bootstrap  // 'succeeded' | 'failed' | undefined
  const ansibleSucceeded = ansibleStatus === 'succeeded'
  const ansibleFailed = ansibleStatus === 'failed'
  const ansibleTriggered = !!deployment.ansible_pipeline_run_url || (hasApp && infraSuccess)

  const appTriggered = !!deployment.app_pipeline_run_url
  const appInProgress = !!appRun && appRun.status !== 'completed'

  // Failure at any chained stage, before the app pipeline was triggered.
  const chainFailed = !!runFailed || ansibleFailed
    || deployment.status === 'failed' || deployment.status === 'rolled_back'
  // The failure was at the infrastructure stage specifically (so nothing
  // downstream — Ansible or AptBackOffice — was ever triggered).
  const infraFailed = !!runFailed || (chainFailed && !infraSuccess && !ansibleFailed)

  const deploying = runInProgress
    || (hasApp && infraSuccess && !chainFailed && !appTriggered)  // ansible / handoff in flight
    || appInProgress || isRunning

  const isSuccess = hasApp
    ? (infraSuccess && appTriggered)
    : isApprise ? deployment.status === 'infra_ready'
    : (pipelineRun ? (runDone && !runFailed) : deployment.status === 'completed')
  const isFailed = hasApp
    ? (chainFailed && !appTriggered)
    : isApprise ? (deployment.status === 'failed' || deployment.status === 'rolled_back')
    : (pipelineRun
        ? !!runFailed
        : (deployment.status === 'failed' || deployment.status === 'rolled_back'))

  // Status shown per deployment item. The backend never advances
  // DeploymentItem.status off its 'pending' default, so derive it from the
  // overall journey — and never surface a premature 'completed' from the
  // deployment record before the app pipeline has actually been triggered.
  const overallStatus = isApprise ? deployment.status
    : isSuccess ? 'completed'
    : isFailed ? 'failed'
    : (hasApp && (infraSuccess || deployment.status === 'completed') && !appTriggered) ? 'deploying_app'
    : deployment.status

  // Phase stepper states — computed here so JSX stays clean (avoids IIFE in TSX).
  const infraPhaseState = (pipelineRun ? runPhase(pipelineRun)
    : (deployment.status === 'failed' || deployment.status === 'rolled_back') ? 'failed'
    : deployment.pipeline_run_url ? 'active'
    : deployment.status === 'pending' ? 'pending' : 'active') as PhaseState
  // Ansible only starts after infra succeeds; stays 'pending' (never started) if infra failed.
  const ansiblePhaseState: PhaseState =
    ansibleSucceeded ? 'done'
    : ansibleFailed ? 'failed'
    : (infraSuccess && ansibleTriggered) ? 'active'
    : 'pending'
  // AptBackOffice only starts after Ansible succeeds.
  const appPhaseState: PhaseState = appRun ? runPhase(appRun)
    : appTriggered ? 'done'
    : ansibleSucceeded ? 'active'
    : 'pending'

  const progress = (() => {
    if (needsApproval) return STATUS_PROGRESS[deployment.status] ?? 5
    if (isApprise) return STATUS_PROGRESS[deployment.status] ?? 0
    if (hasApp) {
      if (appTriggered) return 100                       // handed off to AptBackOffice (1522)
      if (chainFailed) return ansibleFailed ? 80 : infraSuccess ? 60 : 40  // stopped mid-chain
      if (ansibleSucceeded) return 90                    // ansible done, triggering 1522
      if (infraSuccess) return 70                        // infra done, ansible bootstrap running
      // Infrastructure still running — drive the bar from its jobs (0–60%).
      if (!pipelineRun) return STATUS_PROGRESS[deployment.status] ?? 0
      const jobs = pipelineRun.jobs ?? []
      if (!jobs.length) return 12
      const done = jobs.filter((j) => j.status === 'completed').length
      return Math.min(60, Math.max(12, Math.round((done / jobs.length) * 60)))
    }
    if (pipelineRun) {
      if (runDone) return 100
      const jobs = pipelineRun.jobs ?? []
      if (!jobs.length) return 12
      const done = jobs.filter((j) => j.status === 'completed').length
      return Math.min(96, Math.max(12, Math.round((done / jobs.length) * 100)))
    }
    return STATUS_PROGRESS[deployment.status] ?? 0
  })()

  // One-line summary of where the Ross infra→ansible→app chain currently stands,
  // shown under the progress bar. Empty for non-Ross deployments.
  const rossPhaseNote = !hasApp ? ''
    : isFailed
      ? (runFailed
          ? 'Infrastructure failed — the Ansible bootstrap (ADO 2093) and AptBackOffice application (ADO 1522) pipelines were not triggered. No infrastructure was created.'
          : ansibleFailed
            ? 'Ansible bootstrap failed — the AptBackOffice application pipeline (ADO 1522) was not triggered.'
            : 'Deployment failed.')
      : appTriggered
        ? 'AptBackOffice application pipeline triggered — Ross ERP installation is running in Azure DevOps.'
        : ansibleSucceeded
          ? 'Ansible bootstrap complete — triggering the AptBackOffice application pipeline (ADO 1522)…'
          : infraSuccess
            ? 'Infrastructure ready — Ansible bootstrap pipeline (ADO 2093) triggered, waiting for it to complete…'
            : ''

  const progressLabel = (isApprise && isSuccess) ? 'Infrastructure Ready'
    : isSuccess ? 'Deployment Complete'
    : isFailed ? 'Deployment Failed'
    : needsApproval ? 'Awaiting Approval'
    : (isApprise && deployment.status === 'awaiting_plan_approval') ? 'Awaiting Plan Approval'
    : (isApprise && deployment.status === 'cancelled') ? 'Cancelled'
    : (isApprise && deployment.status === 'verifying_server') ? 'Verifying Server…'
    : (hasApp && ansibleSucceeded && !appTriggered) ? 'Triggering Application…'
    : (hasApp && infraSuccess && !ansibleSucceeded) ? 'Running Ansible Bootstrap…'
    : deploying ? 'Deploying…'
    : deployment.status === 'pending' ? 'Pending' : 'Ready'

  return (
    <div className={styles.page}>
      {/* Ticket Modal */}
      {showTicketModal && (
        <TicketModal
          deploymentName={deployment.name ?? ''}
          loading={ticketCreating}
          onSubmit={handleCreateTicket}
          onClose={() => setShowTicketModal(false)}
        />
      )}

      {/* Switch Traffic Confirmation */}
      {showSwitchConfirm && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h2 className="modal-title">Switch Traffic</h2>
            <p className="modal-subtitle">
              Switch live traffic from the{' '}
              <strong>{deployment.active_slot === 'blue' ? 'Blue' : 'Green'}</strong> slot to the{' '}
              <strong>{deployment.active_slot === 'blue' ? 'Green' : 'Blue'}</strong> slot.
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--aptean-text-secondary)', display: 'block', marginBottom: 6 }}>
                Notes (optional)
              </label>
              <input
                style={{ width: '100%', padding: '8px 12px', background: 'var(--aptean-bg-elevated)', border: '1px solid var(--aptean-border)', borderRadius: 6, color: 'var(--aptean-text-primary)', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }}
                placeholder="Reason for switching..."
                value={switchNotes}
                onChange={(e) => setSwitchNotes(e.target.value)}
              />
            </div>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setShowSwitchConfirm(false)}>Cancel</Button>
              <Button loading={switchTraffic.isPending} onClick={confirmSwitchTraffic}>
                Confirm Switch
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className={styles.progressBar}>
        <div className={styles.progressMeta}>
          <span className={styles.progressLabel}>{progressLabel}</span>
          <span className={styles.progressPct}>{progress}%</span>
        </div>
        <div className={styles.progressTrack}>
          <div
            className={clsx(styles.progressFill, isSuccess && styles.progressSuccess, isFailed && styles.progressFailed)}
            style={{ width: `${progress}%` }}
          />
        </div>
        {hasApp && rossPhaseNote && (
          <div style={{ marginTop: 8, fontSize: 13, color: isFailed ? 'var(--aptean-error, #d33)' : 'var(--aptean-text-secondary)' }}>
            {rossPhaseNote}
          </div>
        )}
      </div>

      {/* End-to-end phases: Infrastructure → Ansible bootstrap → AptBackOffice (Ross) */}
      {deployment.has_app_stage && (
        <PhaseStepper
          phases={[
            {
              label: 'Infrastructure',
              state: infraPhaseState,
              url: deployment.pipeline_run_url ?? undefined,
            },
            {
              label: 'Ansible bootstrap',
              state: ansiblePhaseState,
              url: deployment.ansible_pipeline_run_url ?? undefined,
            },
            {
              label: 'AptBackOffice',
              state: appPhaseState,
              url: deployment.app_pipeline_run_url ?? undefined,
            },
          ]}
        />
      )}

      {/* Approval Panel */}
      {needsApproval && (
        <div className={styles.approvalBanner}>
          <ShieldCheck size={16} />
          <div className={styles.approvalBannerContent}>
            <span className={styles.approvalBannerTitle}>Approval Required</span>
            <span>This deployment is awaiting manager approval before it can execute.</span>
          </div>
          <div className={styles.approvalActions}>
            {!showApprovePanel ? (
              <Button size="sm" icon={<ShieldCheck size={13} />} onClick={() => setShowApprovePanel(true)}>
                Review &amp; Approve
              </Button>
            ) : (
              <div className={styles.approvalForm}>
                <input
                  className={styles.approvalInput}
                  placeholder="Approval notes (optional)"
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                />
                <Button size="sm" loading={approving} onClick={handleApprove}>Approve</Button>
                <Button size="sm" variant="danger" loading={approving} onClick={handleReject}>Reject</Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ticket success */}
      {ticketCreated && (
        <div className={styles.ticketBanner}>
          <Ticket size={15} />
          <span>Service desk ticket created: <strong>{ticketCreated}</strong></span>
        </div>
      )}

      {/* Auto-fix banner */}
      {(deployment.auto_fix_attempts ?? 0) > 0 && (
        <div className={styles.autoFixBanner}>
          <Sparkles size={14} />
          Claude AI made {deployment.auto_fix_attempts} auto-fix attempt{(deployment.auto_fix_attempts ?? 0) !== 1 ? 's' : ''} during this deployment
        </div>
      )}

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Button
            variant="ghost"
            size="sm"
            icon={<ArrowLeft size={14} />}
            onClick={() => navigate('/deployments')}
          >
            Back
          </Button>
          <div className={styles.headerInfo}>
            <div className={styles.headerTitle}>
              <h1 className={styles.depName}>{deployment.name}</h1>
              <StatusBadge status={overallStatus} />
            </div>
            <div className={styles.headerMeta}>
              <span>{deployment.customer_name}</span>
              <span className={styles.dot}>·</span>
              <span className={styles.envTag}>{deployment.environment}</span>
              <span className={styles.dot}>·</span>
              <span>{deployment.azure_region}</span>
              <span className={styles.dot}>·</span>
              <span>{formatRelative(deployment.created_at)}</span>
              {deployment.pipeline_type && (
                <>
                  <span className={styles.dot}>·</span>
                  <span className={styles.pipelineTypeTag}>
                    {deployment.pipeline_type === 'github_actions' ? 'GitHub Actions' : 'Azure DevOps'}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className={styles.headerActions}>
          <Button
            variant="secondary"
            size="sm"
            icon={<RefreshCw size={14} />}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
          {!deployment.service_desk_ticket && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Ticket size={14} />}
              onClick={() => setShowTicketModal(true)}
            >
              Create Ticket
            </Button>
          )}
          {deployment.status === 'pending' && !needsApproval && (
            <Button
              size="sm"
              icon={<Rocket size={14} />}
              loading={triggerMutation.isPending}
              onClick={() => triggerMutation.mutate(deploymentId)}
            >
              Trigger
            </Button>
          )}
          {isRunning && (
            <Button
              variant="danger"
              size="sm"
              icon={<XCircle size={14} />}
              loading={cancelMutation.isPending}
              onClick={() => {
                if (window.confirm('Cancel this deployment?')) {
                  cancelMutation.mutate(deploymentId)
                }
              }}
            >
              Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Application live URL — for Ross (separate ADO app deploy) only claim
          "live" once that run actually succeeds, not just when the simulated
          portal stages finish, so the banner never lies while it's still deploying. */}
      {deployment.status === 'completed' && deployment.application_url
        && (!deployment.has_app_stage || appRun?.conclusion === 'success') && (
        <div className={styles.appUrlCard}>
          <CheckCircle2 size={20} className={styles.successIcon} />
          <div>
            <span className={styles.appUrlLabel}>Application is Live</span>
            <a href={deployment.application_url} target="_blank" rel="noopener noreferrer" className={styles.appUrl}>
              {deployment.application_url}
              <ExternalLink size={12} />
            </a>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {(deployment.status === 'failed' || deployment.status === 'rolled_back') &&
        deployment.error_message && (
          <div className={styles.errorBanner}>
            <AlertTriangle size={16} />
            <div className={styles.errorBannerContent}>
              <span className={styles.errorBannerTitle}>Deployment Failed</span>
              <span className={styles.errorBannerMsg}>{deployment.error_message}</span>
            </div>
          </div>
        )}

      {/* Info Grid */}
      <div className={styles.infoGrid}>
        <InfoItem icon={<Server size={14} />} label="Tenant ID" value={deployment.tenant_id} mono />
        <InfoItem icon={<Globe size={14} />} label="Azure Region" value={deployment.azure_region} />
        <InfoItem
          icon={<GitBranch size={14} />}
          label="Strategy"
          value={formatStrategy(deployment.strategy)}
        />
        <InfoItem
          icon={<Clock size={14} />}
          label="Created"
          value={formatDateTime(deployment.created_at)}
        />
        {deployment.started_at && (
          <InfoItem
            icon={<Clock size={14} />}
            label="Started"
            value={formatDateTime(deployment.started_at)}
          />
        )}
        {deployment.completed_at && (
          <InfoItem
            icon={<CheckCircle2 size={14} />}
            label="Completed"
            value={formatDateTime(deployment.completed_at)}
          />
        )}
        {placementCards(deployment).map((card) => (
          <InfoItem key={card.key} icon={<Server size={14} />} label={card.label} value={card.value} mono />
        ))}
        {deployment.ingress_url && (
          <InfoItem
            icon={<ExternalLink size={14} />}
            label="Ingress URL"
            value={deployment.ingress_url}
            link
          />
        )}
      </div>

      {/* Blue-Green Panel */}
      {isBlueGreen && (
        <div className={styles.blueGreenPanel}>
          <h3 className={styles.blueGreenTitle}>Blue-Green Traffic Control</h3>
          <div className={styles.slotStatus}>
            <div className={clsx(styles.slot, deployment.active_slot === 'blue' && styles.slotActive)}>
              <span className={styles.slotEmoji}>🔵</span>
              <span className={styles.slotName}>Blue Slot</span>
              {deployment.active_slot === 'blue' && <span className={styles.liveTag}>LIVE</span>}
            </div>
            <button
              className={styles.switchBtn}
              onClick={handleSwitchTraffic}
              disabled={switchTraffic.isPending || isRunning}
            >
              {switchTraffic.isPending ? <Loader2 size={14} className={styles.spin} /> : null}
              Switch Traffic ⇄
            </button>
            <div className={clsx(styles.slot, deployment.active_slot === 'green' && styles.slotActive)}>
              <span className={styles.slotEmoji}>🟢</span>
              <span className={styles.slotName}>Green Slot</span>
              {deployment.active_slot === 'green' && <span className={styles.liveTag}>LIVE</span>}
            </div>
          </div>
        </div>
      )}

      <div className={styles.mainGrid}>
        {/* Left Column */}
        <div className={styles.leftColumn}>

          {/* ── Apprise VM: tracks, Terraform plan review & approval, server outputs, verification ── */}
          {isApprise && (
            <AppriseDeploymentPanel deploymentId={deploymentId} status={deployment.status} deploymentTracks={deployment.tracks} />
          )}

          {/* ── Infrastructure: live GitHub Actions run ── */}
          <div ref={infraPanelRef}>
            {deployment.pipeline_run_url && deployment.pipeline_type === 'github_actions' ? (
              <PipelineRunPanel
                title="Infrastructure — GitHub Actions"
                run={pipelineRun}
                runUrl={deployment.pipeline_run_url}
                showLink={false}
              />
            ) : deployment.pipeline_run_url ? (
              <Card>
                <CardHeader title="Infrastructure Pipeline Run" />
                <CardContent>
                  <a href={deployment.pipeline_run_url} target="_blank" rel="noopener noreferrer" className={styles.pipelineLink}>
                    Open pipeline run <ExternalLink size={12} />
                  </a>
                </CardContent>
              </Card>
            ) : null}
          </div>

          {/* ── Chain stage: infrastructure failed → nothing downstream triggered ── */}
          {deployment.has_app_stage && !deployment.app_pipeline_run_url && infraFailed && (
            <div ref={appPanelRef}>
              <Card>
                <CardHeader
                  title="Automation — Azure DevOps"
                  subtitle="Not started — infrastructure failed"
                  actions={<div className={styles.liveIndicator}><span>Not started</span></div>}
                />
                <CardContent>
                  <EmptyState
                    icon={<AlertCircle size={28} />}
                    title="Ansible & AptBackOffice pipelines not triggered"
                    description="Infrastructure provisioning failed, so no infrastructure was created — the Ansible bootstrap (Azure DevOps 2093) and the AptBackOffice application deploy (Azure DevOps 1522) were never triggered. Fix the infrastructure run and start a new deployment."
                  />
                </CardContent>
              </Card>
            </div>
          )}

          {/* ── Chain stage: Ansible bootstrap (ADO 2093) triggered, awaiting its callback ── */}
          {deployment.has_app_stage && !deployment.app_pipeline_run_url && infraSuccess
            && !ansibleSucceeded && !ansibleFailed && (
            <div ref={appPanelRef}>
              <Card>
                <CardHeader
                  title="Ansible bootstrap — Azure DevOps 2093"
                  subtitle="Triggered — waiting for it to complete"
                  actions={
                    deployment.ansible_pipeline_run_url ? (
                      <a href={deployment.ansible_pipeline_run_url} target="_blank" rel="noopener noreferrer" className={styles.pipelineLink}>
                        Open in Azure DevOps <ExternalLink size={12} />
                      </a>
                    ) : (
                      <div className={styles.liveIndicator}><span className={styles.liveDot} /><span>Running</span></div>
                    )
                  }
                />
                <CardContent>
                  <LoadingSpinner label="Infrastructure is ready. The Ansible bootstrap pipeline (Azure DevOps 2093) is creating the FIN/MAN database users. The AptBackOffice application deploy (1522) starts automatically once it succeeds." />
                </CardContent>
              </Card>
            </div>
          )}

          {/* ── Chain stage: Ansible bootstrap failed → AptBackOffice not triggered ── */}
          {deployment.has_app_stage && !deployment.app_pipeline_run_url && ansibleFailed && (
            <div ref={appPanelRef}>
              <Card>
                <CardHeader
                  title="Ansible bootstrap — Azure DevOps 2093"
                  subtitle="Failed"
                  actions={
                    deployment.ansible_pipeline_run_url ? (
                      <a href={deployment.ansible_pipeline_run_url} target="_blank" rel="noopener noreferrer" className={styles.pipelineLink}>
                        Open in Azure DevOps <ExternalLink size={12} />
                      </a>
                    ) : <div className={styles.liveIndicator}><span>Failed</span></div>
                  }
                />
                <CardContent>
                  <EmptyState
                    icon={<AlertCircle size={28} />}
                    title="AptBackOffice application pipeline not triggered"
                    description="The Ansible bootstrap (Azure DevOps 2093) failed while creating the FIN/MAN database users, so the AptBackOffice application deploy (Azure DevOps 1522) was not triggered."
                  />
                </CardContent>
              </Card>
            </div>
          )}

          {/* ── Chain stage: Ansible succeeded, handing off to AptBackOffice (ADO 1522) ── */}
          {deployment.has_app_stage && !deployment.app_pipeline_run_url && infraSuccess && ansibleSucceeded && (
            <div ref={appPanelRef}>
              <Card>
                <CardHeader
                  title="AptBackOffice — Azure DevOps 1522"
                  subtitle="Ansible complete — triggering application pipeline"
                  actions={<div className={styles.liveIndicator}><span className={styles.liveDot} /><span>Pending</span></div>}
                />
                <CardContent>
                  <LoadingSpinner label={
                    deployment.pipeline_config?.app_deploy_detail
                    || 'The Ansible bootstrap succeeded. The AptBackOffice application pipeline (Azure DevOps 1522) is being triggered. Polling every 4 seconds.'
                  } />
                </CardContent>
              </Card>
            </div>
          )}

          {/* ── Multi-app-server: one row per app server so the machine count is unambiguous ── */}
          {(deployment.app_pipeline_runs?.length ?? 0) > 1 && (
            <Card>
              <CardHeader
                title={`Ross ERP app servers (${deployment.app_pipeline_runs!.length})`}
                subtitle="The ERP app installs on every app server in parallel, all against the one shared database."
              />
              <CardContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {deployment.app_pipeline_runs!.map((srv) => {
                    const live = appRun?.servers?.find((s) => s.vm === srv.vm)
                    const { text, color } = appServerStatus(live?.status, live?.conclusion)
                    return (
                      <div
                        key={srv.vm || srv.label}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          gap: 12, padding: '10px 12px',
                          border: '1px solid var(--aptean-border, #e5e7eb)', borderRadius: 8,
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                          <span style={{ fontWeight: 600 }}>
                            {srv.label}{srv.primary ? ' · primary' : ''}
                          </span>
                          <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>{srv.vm || '—'}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
                          <span style={{ color, fontWeight: 600, fontSize: '0.8rem' }}>{text}</span>
                          <a href={srv.url} target="_blank" rel="noopener noreferrer" className={styles.pipelineLink}>
                            Open <ExternalLink size={12} />
                          </a>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ── AptBackOffice: live Azure DevOps run (Ross → pipeline 1522) ── */}
          {deployment.app_pipeline_run_url && (
            <div ref={appPanelRef}>
              {appRunFetching && !appRun ? (
                <Card>
                  <CardHeader
                    title="AptBackOffice — Azure DevOps"
                    subtitle="Connecting to pipeline..."
                    actions={<div className={styles.liveIndicator}><span className={styles.liveDot} /><span>Live</span></div>}
                  />
                  <CardContent>
                    <LoadingSpinner label="Fetching live status from Azure DevOps..." />
                  </CardContent>
                </Card>
              ) : appRunError && !appRun ? (
                <Card>
                  <CardHeader
                    title="AptBackOffice — Azure DevOps"
                    subtitle="Could not connect to Azure DevOps"
                    actions={
                      <a href={deployment.app_pipeline_run_url} target="_blank" rel="noopener noreferrer" className={styles.pipelineLink}>
                        Open in Azure DevOps <ExternalLink size={12} />
                      </a>
                    }
                  />
                  <CardContent>
                    <EmptyState
                      icon={<AlertCircle size={28} />}
                      title="Live status unavailable"
                      description="The portal cannot reach Azure DevOps to fetch live job status. The pipeline is running — open it directly to monitor progress."
                      action={
                        <a href={deployment.app_pipeline_run_url} target="_blank" rel="noopener noreferrer" className={styles.pipelineLink}>
                          Open Azure DevOps pipeline <ExternalLink size={12} />
                        </a>
                      }
                    />
                  </CardContent>
                </Card>
              ) : (
                <PipelineRunPanel
                  title="AptBackOffice — Azure DevOps"
                  linkLabel="Open in Azure DevOps"
                  run={appRun}
                  runUrl={deployment.app_pipeline_run_url}
                />
              )}
            </div>
          )}

          {/* Deployment Items */}
          <Card>
            <CardHeader title="Deployment Items" subtitle={`${deployment.items?.length ?? 0} products`} />
            <CardContent className={styles.noPad}>
              {(deployment.items?.length ?? 0) === 0 ? (
                <EmptyState icon={<Package size={28} />} title="No items" />
              ) : (
                <table className={styles.itemsTable}>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Size</th>
                      <th>{deployment.items.some(i => i.product_name?.toLowerCase() === 'ross') || isApprise ? 'Resource Group' : 'Namespace'}</th>
                      <th>{deployment.items.some(i => i.product_name?.toLowerCase() === 'ross') ? 'VM Identifier' : isApprise ? 'Server' : 'Helm Release'}</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deployment.items.map((item, idx) => (
                      <tr key={item.id ?? idx}>
                        <td>
                          <div className={styles.itemProduct}>
                            <Package size={13} className={styles.itemIcon} />
                            {item.product_name ?? `Product #${item.product}`}
                          </div>
                        </td>
                        <td><SizeBadge size={item.size} /></td>
                        <td className={styles.cellMono}>{item.namespace ?? (isApprise ? deployment.server?.resource_group : undefined) ?? '—'}</td>
                        <td className={styles.cellMono}>{item.helm_release ?? (isApprise ? deployment.server?.name : undefined) ?? '—'}</td>
                        <td>
                          <StatusBadge status={overallStatus} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          {/* Logs */}
          <Card>
            <CardHeader
              title="Deployment Logs"
              subtitle={isRunning ? 'Auto-refreshing every 5s' : undefined}
              actions={
                isRunning && (
                  <div className={styles.liveIndicator}>
                    <span className={styles.liveDot} />
                    <span>Live</span>
                  </div>
                )
              }
            />
            <CardContent className={styles.noPad}>
              {logsLoading ? (
                <LoadingSpinner label="Loading logs..." />
              ) : (logs?.length ?? 0) === 0 ? (
                <EmptyState
                  icon={<Terminal size={28} />}
                  title="No logs yet"
                  description="Logs will appear once the deployment starts."
                />
              ) : (
                <div className={styles.logsWrapper}>
                  {logs!.map((log) => (
                    <div key={log.id} className={styles.logEntry}>
                      <span className={styles.logTime}>
                        {formatDateTime(log.timestamp)}
                      </span>
                      <span
                        className={styles.logLevel}
                        style={{ color: LOG_LEVEL_COLORS[log.level] }}
                      >
                        {log.level}
                      </span>
                      <span className={styles.logSource}>{log.source}</span>
                      <span className={styles.logMsg}>{log.message}</span>
                    </div>
                  ))}
                  <div ref={logsEndRef} />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className={styles.rightColumn}>
          <Card>
            <CardHeader title="Deployment Info" />
            <CardContent>
              <div className={styles.sideInfo}>
                <SideInfoRow label="Status" value={formatDeploymentStatus(overallStatus)} />
                {deployment.requested_by_name && (
                  <SideInfoRow label="Requested by" value={deployment.requested_by_name} />
                )}
                {deployment.approval_status && (
                  <SideInfoRow label="Approval" value={deployment.approval_status.replace(/_/g, ' ')} />
                )}
                {deployment.approved_by_name && (
                  <SideInfoRow label="Approved by" value={deployment.approved_by_name} />
                )}
                {deployment.service_desk_ticket && (
                  <SideInfoRow label="Ticket" value={deployment.service_desk_ticket} mono />
                )}
                {(deployment.auto_fix_attempts ?? 0) > 0 && (
                  <SideInfoRow label="Auto-fix attempts" value={String(deployment.auto_fix_attempts)} />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Network info card */}
          {(deployment.vnet_address_space || deployment.pod_cidr) && (
            <Card>
              <CardHeader title="Network Config" />
              <CardContent>
                <div className={styles.sideInfo}>
                  {deployment.vnet_address_space && (
                    <SideInfoRow label="VNet" value={deployment.vnet_address_space} mono />
                  )}
                  {deployment.subnet_prefix && (
                    <SideInfoRow label="Subnet" value={deployment.subnet_prefix} mono />
                  )}
                  {deployment.pod_cidr && (
                    <SideInfoRow label="Pod CIDR" value={deployment.pod_cidr} mono />
                  )}
                  {deployment.service_cidr && (
                    <SideInfoRow label="Service CIDR" value={deployment.service_cidr} mono />
                  )}
                  {deployment.dns_service_ip && (
                    <SideInfoRow label="DNS IP" value={deployment.dns_service_ip} mono />
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── End-to-end phase stepper (Infrastructure → Application) ─────────────────

function runPhase(run: PipelineRun | undefined): PhaseState {
  if (!run || typeof run.status !== 'string') return 'pending'
  if (run.status !== 'completed') return 'active'
  const c = run.conclusion
  if (c === 'failure' || c === 'cancelled' || c === 'timed_out' || c === 'action_required' || c === 'stale') {
    return 'failed'
  }
  return 'done'
}

function PhaseStepper({ phases }: { phases: { label: string; state: PhaseState; url?: string }[] }) {
  return (
    <div className={styles.phaseStepper}>
      {phases.map((p, i) => {
        const inner = (
          <>
            <span className={styles.phaseStepIndex}>
              {p.state === 'done' ? <CheckCircle2 size={12} />
                : p.state === 'failed' ? <XCircle size={12} />
                : p.state === 'active' ? <Loader2 size={12} className={styles.spin} />
                : i + 1}
            </span>
            {p.label}
            {p.url && p.state !== 'pending' && <ExternalLink size={10} style={{ marginLeft: 4, opacity: 0.6 }} />}
          </>
        )
        return (
          <Fragment key={p.label}>
            {p.url && p.state !== 'pending' ? (
              <a
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className={clsx(
                  styles.phaseStep,
                  p.state === 'active' && styles.phaseStepActive,
                  p.state === 'done' && styles.phaseStepDone,
                  p.state === 'failed' && styles.phaseStepFailed,
                  styles.phaseStepLink,
                )}
              >
                {inner}
              </a>
            ) : (
              <div className={clsx(
                styles.phaseStep,
                p.state === 'active' && styles.phaseStepActive,
                p.state === 'done' && styles.phaseStepDone,
                p.state === 'failed' && styles.phaseStepFailed,
              )}>
                {inner}
              </div>
            )}
            {i < phases.length - 1 && (
              <div className={clsx(styles.phaseConnector, p.state === 'done' && styles.phaseConnectorDone)} />
            )}
          </Fragment>
        )
      })}
    </div>
  )
}

// ─── Live GitHub Actions run panel ──────────────────────────────────────────

type DisplayStatus = 'completed' | 'running' | 'failed' | 'pending' | 'skipped'

function ghDisplayStatus(status: GhRunStatus, conclusion: GhConclusion): DisplayStatus {
  if (status === 'in_progress') return 'running'
  if (status === 'queued') return 'pending'
  // completed — only a real "success" is a green tick.
  if (conclusion === 'success') return 'completed'
  // Steps that never ran because an earlier step failed (or were intentionally
  // skipped) must NOT show as success — show them as skipped, not a green tick.
  if (conclusion === 'skipped' || conclusion === 'neutral' || conclusion == null) return 'skipped'
  return 'failed' // failure | cancelled | timed_out | action_required | stale
}

function StatusIcon({ s }: { s: DisplayStatus }) {
  if (s === 'completed') return <CheckCircle2 size={15} />
  if (s === 'running') return <Loader2 size={15} className={styles.spin} />
  if (s === 'failed') return <XCircle size={15} />
  if (s === 'skipped') return <MinusCircle size={15} />
  return <Loader2 size={14} />
}

function JobRow({ job, isLast }: { job: PipelineRunJob; isLast: boolean }) {
  const [expanded, setExpanded] = useState(false)
  const s = ghDisplayStatus(job.status, job.conclusion)
  const duration = formatDuration(job.started_at, job.completed_at)
  return (
    <div className={styles.stageGroup}>
      <div className={clsx(
        styles.stageRow,
        s === 'running' && styles.stageRunning,
        s === 'completed' && styles.stageCompleted,
        s === 'failed' && styles.stageFailed,
        s === 'pending' && styles.stagePending,
      )}>
        <div className={styles.stageLineCol}>
          <div className={clsx(
            styles.stageIconWrapper,
            s === 'completed' && styles.stageIconCompleted,
            s === 'running' && styles.stageIconRunning,
            s === 'failed' && styles.stageIconFailed,
            s === 'pending' && styles.stageIconPending,
          )}>
            <StatusIcon s={s} />
          </div>
          {!isLast && <div className={clsx(styles.stageLine, s === 'completed' && styles.stageLineDone, s === 'running' && styles.stageLineActive)} />}
        </div>
        <div className={styles.stageContent}>
          <div className={styles.stageHeader}>
            <span className={styles.stageName}>{job.name}</span>
            <div className={styles.stageMeta}>
              {duration && <span className={styles.stageDuration}>{duration}</span>}
              {s === 'running' && <span className={styles.stageStatusRunning}>Running…</span>}
              <a href={job.html_url} target="_blank" rel="noopener noreferrer" className={styles.stageLogExpander}>
                <ExternalLink size={11} /> Open
              </a>
            </div>
          </div>
          {job.steps.length > 0 && (
            <button className={styles.stageLogExpander} onClick={() => setExpanded((v) => !v)}>
              <Terminal size={11} />
              {expanded ? 'Hide steps' : `Show ${job.steps.length} steps`}
            </button>
          )}
          {expanded && job.steps.length > 0 && (
            <pre className={styles.stageLogOutput}>
              {job.steps.map((step) => {
                const ss = ghDisplayStatus(step.status, step.conclusion)
                const mark = ss === 'completed' ? '✓' : ss === 'failed' ? '✗' : ss === 'running' ? '▶' : ss === 'skipped' ? '⊘' : '·'
                return `${mark} ${step.name}`
              }).join('\n')}
            </pre>
          )}
        </div>
      </div>
    </div>
  )
}

function PipelineRunPanel({ run, runUrl, title, linkLabel = 'Open run', showLink = true }: {
  run: PipelineRun | undefined; runUrl?: string; title: string; linkLabel?: string; showLink?: boolean
}) {
  const hasRun = !!run && typeof run.status === 'string'
  const runActive = hasRun ? run!.status !== 'completed' : false
  const jobs = hasRun ? (run!.jobs ?? []) : []

  return (
    <Card>
      <CardHeader
        title={title}
        subtitle={hasRun ? `Run #${run!.run_number} · ${run!.status.replace(/_/g, ' ')}` : 'Live pipeline run'}
        actions={
          <div className={styles.headerActions}>
            {runActive && (
              <div className={styles.liveIndicator}>
                <span className={styles.liveDot} />
                <span>Live</span>
              </div>
            )}
            {showLink && runUrl && (
              <a href={runUrl} target="_blank" rel="noopener noreferrer" className={styles.pipelineLink}>
                {linkLabel} <ExternalLink size={12} />
              </a>
            )}
          </div>
        }
      />
      <CardContent>
        {hasRun && jobs.length > 0 ? (
          <div className={styles.pipelineView}>
            {jobs.map((job, idx) => (
              <JobRow key={job.id} job={job} isLast={idx === jobs.length - 1} />
            ))}
          </div>
        ) : hasRun && runActive ? (
          <LoadingSpinner label="Waiting for pipeline jobs..." />
        ) : hasRun ? (
          <EmptyState
            icon={<GitBranch size={28} />}
            title="Jobs unavailable"
            description="The pipeline ran but job details could not be loaded. Open the run directly to see live status."
            action={showLink && runUrl ? (
              <a href={runUrl} target="_blank" rel="noopener noreferrer" className={styles.pipelineLink}>
                Open run <ExternalLink size={12} />
              </a>
            ) : undefined}
          />
        ) : (
          <EmptyState
            icon={<GitBranch size={28} />}
            title="No live run to show"
            description="The pipeline run has not started reporting yet, or it is not a GitHub Actions run. Open the run link to view live status."
          />
        )}
      </CardContent>
    </Card>
  )
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function InfoItem({
  icon, label, value, mono, link,
}: {
  icon: React.ReactNode; label: string; value: string; mono?: boolean; link?: boolean
}) {
  return (
    <div className={styles.infoItem}>
      <div className={styles.infoIcon}>{icon}</div>
      <div className={styles.infoContent}>
        <span className={styles.infoLabel}>{label}</span>
        {link ? (
          <a href={value} target="_blank" rel="noopener noreferrer" className={styles.infoLink}>
            {value}<ExternalLink size={10} />
          </a>
        ) : (
          <span className={clsx(styles.infoValue, mono && styles.infoValueMono)}>{value}</span>
        )}
      </div>
    </div>
  )
}

function SideInfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className={styles.sideInfoRow}>
      <span className={styles.sideInfoLabel}>{label}</span>
      <span className={clsx(styles.sideInfoValue, mono && styles.infoValueMono)}>{value}</span>
    </div>
  )
}

function TicketModal({
  deploymentName, loading, onSubmit, onClose,
}: {
  deploymentName: string
  loading: boolean
  onSubmit: (data: { title: string; description: string; priority: string }) => void
  onClose: () => void
}) {
  const [title, setTitle] = useState(`Deploy: ${deploymentName}`)
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('medium')
  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <h2 className="modal-title">Create Service Desk Ticket</h2>
        <p className="modal-subtitle">Log this deployment as a change-management record.</p>
        <div className={styles.ticketForm}>
          <div className={styles.ticketField}>
            <label className={styles.ticketLabel}>Title</label>
            <input className={styles.ticketInput} value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className={styles.ticketField}>
            <label className={styles.ticketLabel}>Priority</label>
            <select className={styles.ticketInput} value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div className={styles.ticketField}>
            <label className={styles.ticketLabel}>Description</label>
            <textarea className={styles.ticketTextarea} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional details..." />
          </div>
        </div>
        <div className="modal-actions">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button icon={<Ticket size={14} />} loading={loading} onClick={() => onSubmit({ title, description, priority })}>
            Create Ticket
          </Button>
        </div>
      </div>
    </div>
  )
}
